import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Paper, 
  Box, 
  Typography, 
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { treeDeparts } from '../../modules/deptModule/departs';
import { setTargetType, getKpiList } from '../../modules/targetModule/targetKpi';
import DeptTree from '../../components/deptmng/DeptTree';
import _ from 'lodash';

/**
 * 목표 관리 - 조직도 트리 컨테이너
 */
const TargetOrgTreeContainer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { targetTyp: urlTargetTyp, deptId: urlDeptId } = useParams();
  
  const [headquarters, setHeadquarters] = useState([]);
  const [selectedTargetTyp, setSelectedTargetTyp] = useState(urlTargetTyp || 'SALES');
  
  // Redux 상태
  const { departs, loading } = useSelector(({ departModule, loading }) => ({
    departs: departModule.departs.departs,
    loading: loading['depart/TREE_DEPART'],
  }));

  // 목표 유형 옵션
  const targetTypeOptions = [
    { value: 'SALES', label: '영업 목표' },
    { value: 'PRODUCTION', label: '생산 목표' },
    { value: 'TECH', label: '기술 목표' },
    { value: 'QC', label: '품질 목표' },
  ];

  // 목표 유형 변경
  const handleTargetTypeChange = (event) => {
    const newTargetTyp = event.target.value;
    setSelectedTargetTyp(newTargetTyp);
    dispatch(setTargetType(newTargetTyp));
    dispatch(getKpiList({ targetTyp: newTargetTyp, useYn: 'Y' }));
    
    // 부서가 선택되어 있으면 URL 업데이트
    if (urlDeptId) {
      navigate(`/targetmng/${newTargetTyp}/${urlDeptId}`);
    }
  };

  // 조직 선택
  const onHandleClick = (deptId) => {
    navigate(`/targetmng/${selectedTargetTyp}/${deptId}`);
  };

  // 조직도 조회
  useEffect(() => {
    dispatch(treeDeparts({ role_sales: 'SALES' }));
  }, [dispatch]);

  // URL 목표 유형 동기화
  useEffect(() => {
    if (urlTargetTyp && urlTargetTyp !== selectedTargetTyp) {
      setSelectedTargetTyp(urlTargetTyp);
      dispatch(setTargetType(urlTargetTyp));
    }
  }, [urlTargetTyp, dispatch, selectedTargetTyp]);

  // 초기 KPI 목록 조회
  useEffect(() => {
    dispatch(getKpiList({ targetTyp: selectedTargetTyp, useYn: 'Y' }));
  }, [dispatch, selectedTargetTyp]);

  // 조직도 데이터 가공
  useEffect(() => {
    if (departs) {
      let tempData = _.cloneDeep(departs);
      const hqData = tempData?.children?.map((dept) => ({
        parent: dept.parent,
        level: dept.Level,
        id: dept.id,
        name: dept.name,
        children: dept.children,
      }));
      tempData.children = hqData;
      setHeadquarters(tempData);
    }
  }, [departs]);

  if (loading || !departs) {
    return (
      <Paper elevation={2} sx={{ p: 2 }}>
        <Typography>로딩 중...</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={2}>
      {/* 헤더 */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'center',
          backgroundColor: '#424242',
          color: '#fff',
          py: 1,
          px: 2,
        }}
      >
        <AccountTreeIcon sx={{ mr: 1 }} />
        <Typography variant="subtitle2" component="div">
          조직도
        </Typography>
      </Box>
      
      <Divider />
      
      {/* 목표 유형 선택 */}
      <Box sx={{ p: 2 }}>
        <FormControl fullWidth size="small">
          <InputLabel>목표 유형</InputLabel>
          <Select
            value={selectedTargetTyp}
            label="목표 유형"
            onChange={handleTargetTypeChange}
          >
            {targetTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      <Divider />
      
      {/* 조직도 트리 */}
      <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
        <DeptTree nodes={headquarters} onHandleClick={onHandleClick} />
      </Box>
    </Paper>
  );
};

export default TargetOrgTreeContainer;
